import React, { useState, useEffect } from 'react';
import {
  Edit,
  SimpleForm,
  TextInput,
  DateInput,
  ReferenceInput,
  AutocompleteInput,
  FormDataConsumer,
  required,
  useDataProvider,
  useNotify,
  useRedirect,
} from 'react-admin';
import { FormToolbar } from '../../common/FormToolbar';
import PhoneIcon from '@material-ui/icons/Phone';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { OrderItemsGrid } from './OrderItemsGrid';
import { TextField, CircularProgress } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { useForm } from 'react-final-form';
import { CustomerFromMobile } from './CustomerFromMobile';

const DesignSelectWithPhoto = () => {
  const dataProvider = useDataProvider();
  const form = useForm();
  const [options, setOptions] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchDesigns = async () => {
      setLoading(true);
      try {
        const res = await dataProvider.getList('product-groups', {
          pagination: { page: 1, perPage: 100 },
          sort: { field: 'designName', order: 'ASC' },
          filter: {},
        });
        setOptions(res.data || []);
      } catch (e) {
        console.error('Error loading product groups', e);
      } finally {
        setLoading(false);
      }
    };

    fetchDesigns();
  }, [dataProvider]);

  const currentDesignId = form.getState().values.designCode;
  const currentValue = options.find(o => o.id === currentDesignId) || null;

  return (
    <Autocomplete
      options={options}
      getOptionLabel={(option: any) => option?.designName || option?.name || ''}
      value={currentValue}
      onChange={(_e, value: any) => {
        form.change('designCode', value ? value.id : undefined);
      }}
      renderOption={(option: any) => {
        const name = option?.designName || option?.name || '';
        const photo = option?.photograph;
        return (
          <span style={{ display: 'flex', alignItems: 'center' }}>
            {photo ? (
              <img
                src={photo}
                alt={name}
                style={{ width: 36, height: 36, objectFit: 'contain', marginRight: 8, borderRadius: 4, background: '#fff' }}
              />
            ) : null}
            <span>{name}</span>
          </span>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Design"
          variant="outlined"
          fullWidth
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export const OrderEdit = (props: any) => {
  console.log('OrderEdit props:', props);
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [items, setItems] = useState<{ productId: number; qty: number }[]>([]);
  const [selectedDesignCode, setSelectedDesignCode] = useState<string | null>(null);
  const propsObj: any = { ...props };
  const [isCustLogin, setIsCustLogin] = useState(false);

  const handleItemsChange = React.useCallback((items) => {
    setItems(items);
  }, []);

  // Check customer login type
  useEffect(() => {
    const type = localStorage.getItem("type");
    setIsCustLogin(type === "isCustLogin");
  }, []);

  if (propsObj.permissions) {
    const resourcename = `/${props.resource}`;
    const myPermissions = propsObj.permissions.filter((item: any) => item.NavigateUrl.toLowerCase().trim() === resourcename);
    if (myPermissions.length > 0) {
      const p = myPermissions[0];
      propsObj.hasList = p.View === 1;
      propsObj.hasShow = p.View === 1;
      propsObj.hasEdit = p.Edit === 1;
      propsObj.hasDelete = p.Delete === 1;
      propsObj.hasCreate = p.Create === 1;
      propsObj.hasExport = p.Export === 1;
    }
  }

  const handleSubmit = async (values: any) => {
    try {

      // Strip out UI-only fields so they are not sent to the orders endpoint
      const {
        customerMobile,
        newCustomerName,
        newCustomerCode,
        newCustomerState,
        newCustomerAddress1,
        newCustomerAddress2,
        newCustomerAddress3,
        newCustomerPincode,
        isNewCustomerFromMobile,
        ...cleanValues
      } = values;

      // Ensure we have a customerId: if missing but mobile & new customer fields filled, create customer first
      let customerId: number | undefined = values.customerId;
      if (!customerId && customerMobile && newCustomerName) {
        try {
          const createdCustomer: any = await dataProvider.create('customers', {
            data: {
              code: newCustomerCode || customerMobile,
              customerName: newCustomerName,
              mobileNo: customerMobile,
              state: newCustomerState,
              address1: newCustomerAddress1,
              address2: newCustomerAddress2,
              address3: newCustomerAddress3,
              pincode: newCustomerPincode,
            },
          });
          customerId = createdCustomer.data.id;
        } catch (e) {
          const anyErr: any = e;
          notify(anyErr?.message || 'Error creating customer from mobile', { type: 'warning' });
          return;
        }
      }

      const header = {
        ...cleanValues,
        customerId,
        orderDate: values.orderDate
          ? new Date(values.orderDate).toISOString()
          : values.orderDate,
      };
      const id = values.id;
      await dataProvider.update('orders', { id, data: header, previousData: values });

      const existing: any = await dataProvider.getList('order-items', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'id', order: 'ASC' },
        filter: { orderId: id },
      });
      const existingMap = new Map<number, any>();
      for (const row of existing.data) {
        existingMap.set(row.productId, row);
      }

      for (const it of items) {
        const row = existingMap.get(it.productId);
        if (row) {
          if (it.qty > 0) {
            // update qty
            await dataProvider.update('order-items', {
              id: row.id,
              data: { ...row, qty: it.qty },
              previousData: row,
            });
          } else {
            // delete if qty is 0
            await dataProvider.delete('order-items', { id: row.id, previousData: row });
          }
          existingMap.delete(it.productId);
        } else if (it.qty > 0) {
          // new row
          await dataProvider.create('order-items', {
            data: {
              orderId: id,
              productId: it.productId,
              qty: it.qty,
            },
          });
        }
      }

      notify('Order updated', { type: 'info' });
      redirect('/orders');
    } catch (e) {
      console.error(e);
      const anyErr: any = e;
      notify(anyErr?.message || 'Error updating order', { type: 'warning' });
    }
  };

  return (
    <React.Fragment>
      <Edit title="Edit Order" {...props} undoable={false}>
        <SimpleForm
          warnWhenUnsavedChanges
          toolbar={<FormToolbar onSave={handleSubmit} onClose={props.onClose} onCancel={props.onCancel} {...props} {...propsObj} />}
          variant="outlined"
          save={handleSubmit}
        >
          <TextInput source="orderNo" label="Order No" fullWidth disabled />
          <DateInput source="orderDate" label="Order Date" fullWidth />

          <DesignSelectWithPhoto />

          <FormDataConsumer>
            {({ formData }) => (
              formData?.designCode ? (
                <DesignPreview 
                  id={formData.designCode} 
                  dataProvider={dataProvider} 
                  onDesignCodeChange={(designCode) => setSelectedDesignCode(designCode)}
                />
              ) : null
            )}
          </FormDataConsumer>

          {/* Show Customer Dropdown only for Admin */}
          {!isCustLogin && (
            <ReferenceInput
              source="customerId"
              reference="customers"
              perPage={1000}
              sort={{ field: "customerName", order: "ASC" }}
            >
              <AutocompleteInput
                optionText={(record: any) => {
                  if (!record) return '';
                  return `${record.customerName || ''} (${record.mobileNo || ''})`;
                }}
                fullWidth
                validate={required()}
              />
            </ReferenceInput>
          )}

          <CustomerFromMobile />

          {/* New customer details when mobile is new (no customerId yet) */}
          <FormDataConsumer>
            {({ formData }) => {
              const hasMobile = formData?.customerMobile && String(formData.customerMobile).trim().length === 10;
              const isNewFromMobile = !!formData?.isNewCustomerFromMobile;
              if (!hasMobile || !isNewFromMobile) return null;

              return (
                <div style={{ marginTop: 8, padding: 8, border: '1px solid #eee', borderRadius: 4 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>New Customer Details</div>
                  <TextInput source="newCustomerName" label="Customer Name" fullWidth variant="outlined" />
                  <TextInput source="newCustomerCode" label="Customer Code" fullWidth variant="outlined" />
                  <TextInput source="newCustomerState" label="State" fullWidth variant="outlined" />
                  <TextInput source="newCustomerAddress1" label="Address 1" fullWidth variant="outlined" />
                  <TextInput source="newCustomerAddress2" label="Address 2" fullWidth variant="outlined" />
                  <TextInput source="newCustomerAddress3" label="Address 3" fullWidth variant="outlined" />
                  <TextInput source="newCustomerPincode" label="Pincode" fullWidth variant="outlined" />
                </div>
              );
            }}
          </FormDataConsumer>

          {/* Existing customer preview when customerId is resolved */}
          <FormDataConsumer>
            {({ formData }) => (
              formData?.customerId ? (
                <CustomerPreview id={formData.customerId} dataProvider={dataProvider} />
              ) : null
            )}
          </FormDataConsumer>

          {/* Order Items Grid */}
          {selectedDesignCode && (
            <FormDataConsumer>
              {({ formData }) => (
                <OrderItemsGrid
                  designCode={formData.designCode}
                  orderId={formData.id}
                  onChange={handleItemsChange}   // Using handleItemsChange
                  selectedDesignCode={selectedDesignCode}
                />
              )}
            </FormDataConsumer>
          )}
        </SimpleForm>
      </Edit>
    </React.Fragment>
  );
};

// CustomerPreview & DesignPreview (unchanged)
const CustomerPreview = ({ id, dataProvider }: { id: any; dataProvider: any }) => {
  const [rec, setRec] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    if (!id) return;
    dataProvider
      .getOne('customers', { id })
      .then((r: any) => {
        if (mounted) setRec(r.data);
      })
      .catch(() => {
        if (mounted) setRec(null);
      });
    return () => { mounted = false; };
  }, [id, dataProvider]);

  if (!rec) return null;
  const fields = [
    { label: 'Mobile', key: 'mobileNo' },
    { label: 'Phone', key: 'phone' },
    { label: 'City', key: 'city' },
    { label: 'District', key: 'district' },
    { label: 'State', key: 'state' },
    { label: 'Postal', key: 'postalCode' },
    { label: 'Country', key: 'country' },
  ];
  const contact = [rec.address1, rec.address2].filter((x) => x && x.trim()).join(', ');

  return (
    <div style={{ marginTop: 8, padding: 10, border: '1px solid #eee', borderRadius: 6, background: '#fbfdff' }}>
      <div style={{ fontWeight: 600, color: '#0b5394' }}>{rec.customerName || rec.userName}</div>
      {contact ? (
        <div style={{ marginTop: 6, padding: '6px 8px', background: '#e8f4f8', borderRadius: 4, color: '#0b5566' }}>
          <strong style={{ marginRight: 6 }}>Address:</strong>
          <span>{contact}</span>
        </div>
      ) : null}
      <div style={{ marginTop: 6 }}>
        {fields.map((f) => {
          const v = rec[f.key];
          if (!v) return null;
          if (f.key === 'mobileNo') {
            return (
              <div key={f.key} style={{ color: '#444', fontSize: 13, display: 'flex', alignItems: 'center' }}>
                <PhoneIcon style={{ fontSize: 16, color: '#0b5394', marginRight: 6 }} />
                <strong style={{ marginRight: 6 }}>{f.label}:</strong> {v}
              </div>
            );
          }
          if (f.key === 'state') {
            return (
              <div key={f.key} style={{ color: '#444', fontSize: 13, display: 'flex', alignItems: 'center' }}>
                <LocationOnIcon style={{ fontSize: 16, color: '#0b5394', marginRight: 6 }} />
                <strong style={{ marginRight: 6 }}>{f.label}:</strong> {v}
              </div>
            );
          }
          return (
            <div key={f.key} style={{ color: '#444', fontSize: 13 }}>
              <strong style={{ marginRight: 6 }}>{f.label}:</strong> {v}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const DesignPreview = ({ id, dataProvider, onDesignCodeChange }: { id: any; dataProvider: any; onDesignCodeChange: (designCode: string) => void }) => {
  const [rec, setRec] = useState<any>(null);
  useEffect(() => {
    let mounted = true;
    if (!id) return;
    dataProvider
      .getOne('product-groups', { id })
      .then((r: any) => {
        if (mounted) {
          setRec(r.data);
          onDesignCodeChange(r.data.designCode);
        }
      })
      .catch(() => {
        if (mounted) setRec(null);
      });
    return () => { mounted = false; };
  }, [id, dataProvider]);

  if (!rec) return null;
  const src = rec.photograph || '';
  if (!src) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', marginTop: 8 }}>
      <img src={src} alt={rec.designName || rec.name} style={{ width: 72, height: 72, objectFit: 'contain', background: '#fff', borderRadius: 6 }} />
      <div style={{ marginLeft: 8 }}>{rec.designName || rec.name}</div>
    </div>
  );
};